import React, { useEffect, useState, useRef } from 'react';
import { pxToRem } from '../util';
import { Column, ConfigProvider } from '@ant-design/charts';
import { IconCaretLeft, IconCaretRight } from '@arco-design/web-react/icon';
import CardHeader from './CardHeader';

const Box1 = (props: { charts: any }) => {
  const color = 'rgb(206, 234, 255)';
  const [data, setData] = useState([]);
  const [allData, setAllData] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const timerRef = useRef(null);
  const manualSwitchRef = useRef(false);

  // 初始化数据
  useEffect(() => {
    setAllData(props.charts?.criticalIllnessRiskComparison || []);
    if ((props.charts?.criticalIllnessRiskComparison || []).length > 0) {
      setData(
        (props.charts?.criticalIllnessRiskComparison || [])[0]?.list || []
      );
    }
  }, [props.charts]);

  // 在组件顶部添加 ref
  const currentIndexRef = useRef(currentIndex);

  // 在 switchData 中更新 ref
  const switchData = (index) => {
    if (allData.length === 0) return;

    // 修复：使用简单的循环逻辑
    let newIndex = index;
    if (index >= allData.length) {
      newIndex = 0; // 超出则回到开头
    } else if (index < 0) {
      newIndex = allData.length - 1; // 小于0则到末尾
    }

    setCurrentIndex(newIndex);
    setData(allData[newIndex].list);

    // 更新 ref
    currentIndexRef.current = newIndex;

    // 如果是手动切换，重置定时器
    if (manualSwitchRef.current) {
      clearTimer();
      startTimer();
      manualSwitchRef.current = false;
    }
  };

  // 修改 startTimer
  const startTimer = () => {
    clearTimer();
    timerRef.current = setInterval(() => {
      // 使用 ref 获取最新的 currentIndex
      switchData(currentIndexRef.current + 1);
    }, 5e3);
  };

  // 添加 useEffect 同步 ref
  useEffect(() => {
    currentIndexRef.current = currentIndex;
  }, [currentIndex]);

  // 清除定时器
  const clearTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  // 处理手动切换
  const handleManualSwitch = (direction) => {
    manualSwitchRef.current = true;
    if (direction === 'prev') {
      switchData(currentIndex - 1);
    } else {
      switchData(currentIndex + 1);
    }
  };

  // 初始化定时器
  useEffect(() => {
    if (allData.length > 0) {
      startTimer();
    }
    return () => clearTimer();
  }, [allData]);
  const config = {
    scale: {
      color: {
        type: 'ordinal',
        range: ['#722ed1', '#ff7875', '#ffc069', '#95de64', '#8c8c8c'],
      },
    },
    data: data,
    xField: 'date',
    yField: 'total',
    colorField: 'name',
    group: {
      padding: 0,
    },
    interval: {
      style: {
        maxWidth: 20,
      },
    },
    interaction: {
      elementHighlight: {
        background: true,
      },
    },
    legend: {
      color: {
        title: false,
        position: 'bottom',
        rowPadding: 5,
      },
    },
  };

  const statitics = [
    {
      label: '已评估',
      value: props.charts?.otherDistribution?.evaluatedTotal || 0,
      suffix: '人',
    },
    {
      label: '评估中',
      value: props.charts?.otherDistribution?.toBeEvaluatedTotal || 0,
      suffix: '人',
    },
    {
      label: '重疾已确诊',
      value:
        props.charts?.otherDistribution?.criticalIllnessDiagnosedTotal || 0,
      suffix: '人',
    },
    {
      label: '重疾有明显风险',
      value:
        props.charts?.otherDistribution?.criticalIllnessObviousRiskTotal || 0,
      suffix: '人',
    },
  ];

  return (
    <div
      style={{
        background:
          'linear-gradient(0deg, rgb(21, 35, 48) 2%, rgb(17, 26, 40) 100%)',
        height: `calc(57vh + 5px)`,
        maxHeight: `calc(57vh + 5px)`,
        overflow: 'hidden',
        padding: '5px',
        boxSizing: 'border-box',
        fontFamily: '阿里妈妈数黑体 Bold',
      }}
    >
      <CardHeader
        title="重疾风险评估情况概览"
        subtitle="Overview of Critical Illness Risk Assessment"
      />

      <div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          {statitics.map((item) => {
            return (
              <div
                key={item.label}
                style={{
                  backgroundColor: 'rgba(10, 115, 255, 0.12)',
                  width: '24%',
                  padding: pxToRem(6),
                  boxSizing: 'border-box',
                  marginTop: '10px',
                  textAlign: 'center',
                }}
              >
                <div style={{ fontSize: pxToRem(16), color: color }}>
                  {item.label}
                </div>
                <div
                  style={{
                    fontSize: pxToRem(24),
                    color: 'rgb(61, 171, 255)',
                    fontWeight: 500,
                  }}
                >
                  {item.value.toLocaleString()}
                  {item.suffix && (
                    <span style={{ fontSize: pxToRem(14), marginLeft: '2px' }}>
                      {item.suffix}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        {/* 切换按钮区域 */}
        {allData.length > 0 && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              marginTop: pxToRem(10),
              marginBottom: pxToRem(10),
              gap: pxToRem(0),
            }}
          >
            <button
              onClick={() => handleManualSwitch('prev')}
              style={{
                background: 'rgba(10, 115, 255, 0.3)',
                color: color,
                border: 'none',
                borderRadius: pxToRem(4),
                padding: pxToRem(3) + ' ' + pxToRem(6),
                cursor: 'pointer',
                fontSize: pxToRem(14),
              }}
            >
              <IconCaretLeft style={{ fontSize: pxToRem(20) }} />
            </button>

            <div
              style={{
                color: 'rgb(61, 171, 255)',
                fontSize: pxToRem(16),
                fontWeight: 'bold',
                padding: '0 10px',
                textAlign: 'center',
              }}
            >
              {allData[currentIndex]?.name || '暂无数据'}
            </div>

            <button
              onClick={() => handleManualSwitch('next')}
              style={{
                background: 'rgba(10, 115, 255, 0.3)',
                color: color,
                border: 'none',
                borderRadius: pxToRem(4),
                padding: pxToRem(3) + ' ' + pxToRem(6),
                cursor: 'pointer',
                fontSize: pxToRem(14),
              }}
            >
              <IconCaretRight style={{ fontSize: pxToRem(20) }} />
            </button>
          </div>
        )}
        <div style={{ height: '40vh' }}>
          <ConfigProvider
            common={{
              theme: {
                type: 'dark',
                axis: {
                  labelFill: color,
                },
              },
            }}
          >
            <Column {...config} />
          </ConfigProvider>
        </div>
      </div>
    </div>
  );
};

export default Box1;
